import { requiresMandatoryLOs } from "../utils/completionCriteria";
import { CourseWizardData } from "./types";
import { DIGITED_CODE_MAX_LENGTH } from "../constants";
import {
  ValidationResult,
  WizardStepErrors,
} from "@components/WizardCst/types";

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object" && "id" in value) {
    return !(value as { id: unknown }).id;
  }
  return false;
};

type Rule = (data: CourseWizardData) => { field: string; error: string } | null;

const required =
  (key: keyof CourseWizardData): Rule =>
  (d) =>
    isEmpty(d[key]) ? { field: key as string, error: "required" } : null;

const maxLength =
  (key: keyof CourseWizardData, max: number): Rule =>
  (d) => {
    const v = d[key];
    return typeof v === "string" && v.length > max
      ? { field: key as string, error: "too-long" }
      : null;
  };

const RULES_BY_STEP: Record<string, Rule[]> = {
  "main-info": [
    required("title"),
    required("detail_description"),
    maxLength("title", 70),
    maxLength("detail_description", 700),
    maxLength("digited_code", DIGITED_CODE_MAX_LENGTH),

    required("propaedeutic_logics"),

    required("completion_criterion"),
    (d) =>
      !requiresMandatoryLOs(d.completion_criterion) &&
      isEmpty(d.completion_value)
        ? { field: "completion_value", error: "required" }
        : null,

    (d) => {
      if (
        requiresMandatoryLOs(d.completion_criterion) ||
        isEmpty(d.completion_value)
      ) {
        return null;
      }
      const n = Number(d.completion_value);
      if (!Number.isFinite(n) || n < 0) {
        return { field: "completion_value", error: "invalid-number" };
      }

      if (
        (d.completion_criterion === "MIN_COMPLETION_PERC" ||
          d.completion_criterion === "COMPLETION_MINUTES_PERC") &&
        n > 100
      ) {
        return { field: "completion_value", error: "invalid-percentage" };
      }
      return null;
    },

    required("cover_id"),

    (d) =>
      d.is_standalone && d.schedule_publication && isEmpty(d.publish_date)
        ? { field: "publish_date", error: "required" }
        : null,

    (d) =>
      d.is_standalone && d.schedule_obsolescence && isEmpty(d.obsolescent_date)
        ? { field: "obsolescent_date", error: "required" }
        : null,
  ],

  "additional-info": [
    (d) => {
      if (isEmpty(d.credits)) return null;
      const n = Number(d.credits);
      if (Number.isFinite(n) && (n < 0 || n > 1000)) {
        return { field: "credits", error: "credits-out-of-range" };
      }
      return null;
    },

    (d) => {
      for (let i = 0; i < d.useful_links.length; i += 1) {
        const link = d.useful_links[i];
        if (isEmpty(link.url)) {
          return { field: `useful_links.${i}.url`, error: "required" };
        }
        if (isEmpty(link.url_label)) {
          return { field: `useful_links.${i}.url_label`, error: "required" };
        }
      }
      return null;
    },
  ],
  "add-activities": [
    (d) =>
      d.selected_activities.length === 0
        ? { field: "selected_activities", error: "at-least-one-activity" }
        : null,
  ],
  "tests-survey": [
    (d) =>
      d.selected_activities.length === 0
        ? { field: "selected_activities", error: "at-least-one-activity" }
        : null,

    (d) =>
      d.test_id && !d.test_logic
        ? { field: "test_logic", error: "required" }
        : null,

    (d) => {
      if (d.test_logic !== "WITH_ZEROING") return null;
      if (isEmpty(d.max_attempt)) {
        return { field: "max_attempt", error: "required" };
      }
      const n = Number(d.max_attempt);
      if (!Number.isFinite(n) || n < 1) {
        return { field: "max_attempt", error: "invalid-number" };
      }
      return null;
    },

    (d) => {
      if (isEmpty(d.test_min_score)) return null;
      const n = Number(d.test_min_score);
      if (!Number.isFinite(n) || n < 0) {
        return { field: "test_min_score", error: "invalid-number" };
      }
      return null;
    },
  ],
  "associate-initiative": [],
  editorial: [],
};

const runStep = (stepKey: string, data: CourseWizardData): ValidationResult => {
  const errors: WizardStepErrors = {};
  for (const rule of RULES_BY_STEP[stepKey] ?? []) {
    const r = rule(data);
    if (r && !errors[r.field]) errors[r.field] = r.error;
  }
  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateMainInfo = (d: CourseWizardData) =>
  runStep("main-info", d);
export const validateAdditionalInfo = (d: CourseWizardData) =>
  runStep("additional-info", d);
export const validateAddActivities = (d: CourseWizardData) =>
  runStep("add-activities", d);
export const validateTestsSurvey = (d: CourseWizardData) =>
  runStep("tests-survey", d);
export const validateAssociateInitiative = (d: CourseWizardData) =>
  runStep("associate-initiative", d);
export const validateEditorial = (d: CourseWizardData) =>
  runStep("editorial", d);
